import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import Seo from "../components/site/Seo";
import { Container } from "../components/site/Ui";
import { Logo } from "../components/site/Navbar";
import { useAuth } from "../lib/AuthContext";
import { errMessage } from "../lib/api";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { useLang } from "../lib/LangContext";


const field =
  "w-full rounded-xl border border-slate-300 px-4 py-3 text-base text-slate-900 outline-none transition-colors focus:border-[#F95738] focus:ring-2 focus:ring-[#F95738]/20";

export default function Login() {
  const { tr } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "", remember: true });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = tr("Please enter a valid email address.");
    if (!form.password) next.password = tr("Please enter your password.");
    setErrors(next);
    if (Object.keys(next).length) return;

    setBusy(true);
    try {
      const user = await login(form.email, form.password);
      toast.success("Welcome back.");
      const target = location.state?.from || (user.role === "admin" ? "/admin" : "/dashboard");
      navigate(target, { replace: true });
    } catch (err) {
      toast.error(errMessage(err));
      setErrors({ form: errMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Login" description="Log in to your PostMint dashboard to review content, upload media and manage your subscription." path="/login" />
      <Container className="flex min-h-[70vh] items-center justify-center py-16">
        <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-7 soft-shadow sm:p-9">
          <Logo />
          <h1 className="mt-6 text-3xl font-bold text-slate-900">{tr("Welcome back.")}</h1>
          <p className="mt-2 text-sm text-slate-600">{tr("Log in to review your content and manage your account.")}</p>

          <div className="mt-7">
            <GoogleSignInButton label={tr("Continue with Google")} testId="login-google-button" />
          </div>

          <div className="mt-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-slate-200" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{tr("or use email")}</span>
            <span className="h-px flex-1 bg-slate-200" aria-hidden="true" />
          </div>

          <form onSubmit={submit} noValidate className="mt-7 space-y-5" data-testid="login-form">
            <div>
              <label htmlFor="login-email" className="text-sm font-semibold text-slate-800">{tr("Email")}</label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                data-testid="login-email-input"
                className={`mt-2 ${field}`}
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
              {errors.email && <p data-testid="login-email-error" className="mt-1.5 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="login-password" className="text-sm font-semibold text-slate-800">{tr("Password")}</label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                data-testid="login-password-input"
                className={`mt-2 ${field}`}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              />
              {errors.password && <p data-testid="login-password-error" className="mt-1.5 text-sm text-red-600">{errors.password}</p>}
            </div>

            <div className="flex items-center justify-between">
              <label htmlFor="login-remember" className="inline-flex items-center gap-2 text-sm text-slate-600">
                <input
                  id="login-remember"
                  type="checkbox"
                  data-testid="login-remember-checkbox"
                  className="h-4 w-4 accent-[#F95738]"
                  checked={form.remember}
                  onChange={(e) => setForm((f) => ({ ...f, remember: e.target.checked }))}
                />
                {tr("Remember me")}
              </label>
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-sm font-semibold text-[#F95738] hover:underline">
                {tr("Forgot password?")}
              </Link>
            </div>

            {errors.form && (
              <p data-testid="login-form-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                {errors.form}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full rounded-full bg-[#F95738] px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#E0482B] disabled:opacity-60"
            >
              {busy ? tr("Logging in…") : tr("Login")}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            {tr("Don't have an account?")}{" "}
            <Link to="/get-started" data-testid="login-get-started-link" className="font-semibold text-[#F95738] hover:underline">
              {tr("Get Started")}
            </Link>
          </p>
        </div>
      </Container>
    </>
  );
}
